// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyCustomer, TyInterest, TyLead, TyProject, TyUnit } from "@/types";
import { readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import DateTimeInput from "@/UI/inputs/DateTimeInput";
import FileInput from "@/UI/inputs/FileInput";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import NumberInput from "@/UI/inputs/NumberInput";
import SelectInput from "@/UI/inputs/SelectInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyCloseDealDetails = {
    /** Lead-first: the person is known and the question is which unit they took. */
    lead?: TyLead;
    /** Unit-first: the unit is known and the question is who took it. Exactly one of the two. */
    unit?: TyUnit;
    /** Which of the two acts to open on, when the caller already knows. */
    mode?: "reserve" | "sell";
    /** For `sell`, from the unit's own offer — a flat listed to let is not being sold. */
    type?: "sale" | "rent";
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/** A hold runs for two weeks unless somebody says otherwise — long enough to get paperwork together. */
const HOLD_DAYS = 14;

/** Read once per mount, through a lazy initialiser: a clock read in a render body is not pure. */
const holdUntil = (): string => {
    const when = new Date();
    when.setDate(when.getDate() + HOLD_DAYS);
    return when.toISOString();
};

/**
 * Winning a deal.
 *
 * **`won` is not a stage somebody types, and this popup is why.** It is in `LEAD_SYSTEM_STAGES`, the
 * stage route refuses it, and the only two things that write it are a reservation and a contract —
 * so pressing `won` on the trail does not move a stage, it asks which of those two actually
 * happened. A pipeline whose win rate can be typed reports whatever somebody wanted it to say.
 *
 * Two tabs for the two answers, because they are genuinely different acts with different fields and
 * different consequences: a hold is reversible and dated to run out, a contract moves the unit, the
 * customer's status and the money. The tab is the first thing asked and everything below follows it.
 *
 * **The unit comes from what they already wanted.** A lead carries the things this person asked
 * about, so those are offered first and any other unit is one more press away — picking from a list
 * of every flat the company owns is how the wrong one gets sold.
 *
 * **The signed paper is picked here, and uploaded after.** A contract exists the moment it is
 * filed, so the form never waits on bytes: the record is written, the scan is handed to the upload
 * tray against the contract it belongs to, and the popup closes. A paper that never arrives leaves
 * the contract saying so on its own popup, which is where it can still be fixed.
 *
 * **It is opened from both ends.** A lead knows the person and asks which unit; a unit's own status
 * trail knows the unit and asks who. Everything between those two questions — the two acts, the
 * money, the dates, the schedule, and the two endpoints they post to — is identical, so it is one
 * form that swaps its first field rather than two forms that drift apart by next quarter.
 */
export default function CloseDealPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { lead, unit: onUnit, mode: openOn, type: openType, onDone } = details as TyCloseDealDetails;

    /** Which end is already known. The form asks for the other one and is otherwise the same. */
    const isUnitFirst = !lead && !!onUnit;

    const leadCustomer = lead && typeof lead.customer === "object" && lead.customer ? lead.customer : null;

    /** Everything this enquiry already names: its own unit, and every unit in its interests. */
    const wanted = [
        lead && typeof lead.unit === "object" && lead.unit ? { _id: lead.unit._id, number: lead.unit.number } : null,
        ...(lead?.interests ?? []).map((entry: TyInterest) =>
            typeof entry.unit === "object" && entry.unit ? { _id: entry.unit._id, number: entry.unit.number } : null
        ),
    ].filter((entry): entry is { _id: string; number: string } => !!entry);
    const suggested = wanted.filter((entry, index) => wanted.findIndex((other) => other._id === entry._id) === index);

    const [mode, setMode] = useState<"reserve" | "sell">(openOn ?? "reserve");
    const [isBrowsing, setIsBrowsing] = useState(!suggested.length);
    const [project, setProject] = useState(lead && typeof lead.project === "object" && lead.project ? lead.project._id : "");
    const [unit, setUnit] = useState(onUnit?._id ?? suggested[0]?._id ?? "");

    /** Unit-first: who took it. A lead already answers this and never shows the picker. */
    const [customerId, setCustomerId] = useState("");
    const [customerSearch, setCustomerSearch] = useState("");

    const [price, setPrice] = useState<number | null>(onUnit?.price ?? lead?.budgetMax ?? null);
    const [deposit, setDeposit] = useState<number | null>(null);
    const [expiresAt, setExpiresAt] = useState<string | null>(holdUntil);

    const [type, setType] = useState<"sale" | "rent">(openType ?? "sale");
    const [signedAt, setSignedAt] = useState<string | null>(() => new Date().toISOString());
    const [endsAt, setEndsAt] = useState<string | null>(null);

    /** The signed agreement, picked now and uploaded once the contract has an id to hang off. */
    const [paper, setPaper] = useState<File[]>([]);

    const [planCount, setPlanCount] = useState<number | null>(null);
    const [planPeriod, setPlanPeriod] = useState<number | null>(1);
    const [planStart, setPlanStart] = useState<string | null>(() => new Date().toISOString());

    const projects = useFetch<{ projects: TyProject[] }>("/properties/projects", { query: { limit: constants.maxPageSize } });
    const units = useFetch<{ units: TyUnit[] }>("/properties/units", {}, false);
    const customers = useFetch<{ customers: TyCustomer[] }>("/crm/customers", {}, false);
    const save = useFetch<{ contract?: { _id: string } }>("/sales/reservations", {}, false);
    const enqueue = useUploads((state) => state.enqueue);

    /**
     * Only the unit-first form asks. The search goes to the API rather than filtering a page of a
     * hundred here: a company with four hundred customers would never see the one it wants in a
     * dropdown, and the list this feeds is the only thing standing between a deal and the wrong name.
     */
    useEffect(() => {
        if (!isUnitFirst) return;
        customers.invoke({ query: { ...(customerSearch.trim() ? { search: customerSearch.trim() } : {}), limit: constants.maxPageSize } });
    }, [isUnitFirst, customerSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    // The unit list is the chosen project's, and only the ones still on the shelf.
    useEffect(() => {
        if (!project) return;
        units.invoke({ query: { project, status: "available", limit: constants.maxPageSize } });
    }, [project]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!save.succeededAt) return;
        // The record exists now, so the scan has something to be owned by. The tray takes it from
        // here — a form that waited for an upload would hold a deal open on a hotel wifi.
        const contractId = save.data?.contract?._id;
        if (contractId && paper.length) enqueue(paper, { model: "Contract", id: contractId, slot: "paper" });
        onClose();
        onDone();
    }, [save.succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    /** The person the deal is with, from whichever end the form was opened. */
    const buyer = isUnitFirst ? customerId : (leadCustomer?._id ?? "");

    const handleSubmit = () => {
        if (!unit || !buyer) return;

        if (mode === "reserve") {
            save.invoke({
                method: "POST",
                path: "/sales/reservations",
                data: { unit, customer: buyer, lead: lead?._id ?? null, price, deposit, expiresAt },
            });
            return;
        }

        save.invoke({
            method: "POST",
            path: "/sales/contracts",
            data: {
                unit,
                customer: buyer,
                lead: lead?._id ?? null,
                type,
                amount: price,
                signedAt,
                startsAt: type === "rent" ? signedAt : null,
                endsAt: type === "rent" ? endsAt : null,
                // Only when a schedule was actually asked for — see the contracts controller.
                plan: planCount && planCount > 0 ? { count: planCount, periodMonths: planPeriod ?? 1, startAt: planStart, totalAmount: price } : null,
            },
        });
    };

    const projectOptions = [
        { value: "", label: t.crm.notSet },
        ...(projects.data?.projects ?? []).map((item) => ({ value: item._id, label: readLangValue(item.name, lang) || t.crm.unnamed })),
    ];
    const unitRows = (units.data?.units ?? []).filter((item) => (typeof item.project === "object" ? item.project?._id : item.project) === project);
    const unitOptions = [{ value: "", label: t.crm.notSet }, ...unitRows.map((item) => ({ value: item._id, label: item.number }))];

    return (
        <section className="side-form side-form--center close-deal">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.sales.closeDeal}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {/* Which of the two things happened. Everything below it follows this answer. */}
                <div className="close-deal__modes" role="group">
                    <button
                        type="button"
                        className={`close-deal__mode${mode === "reserve" ? " close-deal__mode--on" : ""}`}
                        onClick={() => setMode("reserve")}
                        aria-pressed={mode === "reserve"}
                        disabled={save.loading}
                    >
                        <Icon name="history" size={18} />
                        <span>{t.sales.byReservation}</span>
                    </button>
                    <button
                        type="button"
                        className={`close-deal__mode${mode === "sell" ? " close-deal__mode--on" : ""}`}
                        onClick={() => setMode("sell")}
                        aria-pressed={mode === "sell"}
                        disabled={save.loading}
                    >
                        <Icon name="check" size={18} />
                        <span>{t.sales.bySale}</span>
                    </button>
                </div>

                <hr className="close-deal__rule" />

                {/* Unit-first: the unit is settled, so the one open question is who took it. */}
                {isUnitFirst ? (
                    <>
                        <p className="close-deal__fixed">
                            {t.properties.fields.number} · <strong>{onUnit?.number}</strong>
                        </p>
                        <FilterSearchInput
                            value={customerSearch}
                            onChange={setCustomerSearch}
                            label={t.common.search}
                            placeholder={t.crm.searchCustomers}
                        />
                        <SelectInput
                            value={customerId}
                            onChange={setCustomerId}
                            options={[
                                { value: "", label: t.crm.notSet },
                                ...(customers.data?.customers ?? []).map((item) => ({ value: item._id, label: `${item.name} · ${item.phone}` })),
                            ]}
                            label={t.sales.pickCustomer}
                            disabled={save.loading}
                        />
                    </>
                ) : suggested.length > 0 && !isBrowsing ? (
                    <>
                        <SelectInput
                            value={unit}
                            onChange={setUnit}
                            options={suggested.map((item) => ({ value: item._id, label: item.number }))}
                            label={t.sales.pickFromInterests}
                            disabled={save.loading}
                        />
                        <button type="button" className="close-deal__switch" onClick={() => setIsBrowsing(true)} disabled={save.loading}>
                            {t.sales.pickAnyUnit}
                        </button>
                    </>
                ) : (
                    <>
                        <SelectInput
                            value={project}
                            onChange={(next) => {
                                setProject(next);
                                setUnit("");
                            }}
                            options={projectOptions}
                            label={t.crm.fields.project}
                            disabled={save.loading}
                        />
                        <SelectInput
                            value={unit}
                            onChange={setUnit}
                            options={unitOptions}
                            label={t.sales.pickUnit}
                            disabled={save.loading || !project}
                        />
                        {suggested.length > 0 && (
                            <button type="button" className="close-deal__switch" onClick={() => setIsBrowsing(false)} disabled={save.loading}>
                                {t.sales.pickFromInterests}
                            </button>
                        )}
                    </>
                )}

                {mode === "reserve" ? (
                    <>
                        <div className="side-form__row side-form__row--pair">
                            <NumberInput
                                value={price}
                                onChange={setPrice}
                                label={t.properties.fields.price}
                                suffix={t.properties.currency}
                                disabled={save.loading}
                            />
                            <NumberInput
                                value={deposit}
                                onChange={setDeposit}
                                label={t.sales.deposit}
                                suffix={t.properties.currency}
                                disabled={save.loading}
                            />
                        </div>
                        <DateTimeInput
                            value={expiresAt}
                            onChange={setExpiresAt}
                            label={t.sales.expiresAt}
                            dateOnly
                            min={new Date()}
                            disabled={save.loading}
                        />
                    </>
                ) : (
                    <>
                        <SelectInput
                            value={type}
                            onChange={(next) => setType(next as "sale" | "rent")}
                            options={constants.unitOffers.map((item) => ({ value: item, label: t.properties.offers[item] }))}
                            label={t.properties.fields.offer}
                            disabled={save.loading}
                        />

                        <div className="side-form__row side-form__row--pair">
                            <NumberInput
                                value={price}
                                onChange={setPrice}
                                label={t.sales.amount}
                                suffix={t.properties.currency}
                                disabled={save.loading}
                            />
                            <DateTimeInput value={signedAt} onChange={setSignedAt} label={t.sales.signedAt} dateOnly disabled={save.loading} />
                        </div>

                        {type === "rent" && (
                            <DateTimeInput
                                value={endsAt}
                                onChange={setEndsAt}
                                label={t.sales.endsAt}
                                dateOnly
                                min={new Date()}
                                disabled={save.loading}
                            />
                        )}

                        {/* One paper, not a batch: the receipts and anything else are filed onto the
                            contract afterwards, where they can be told apart from the agreement. */}
                        <div className="side-form__group">
                            <span className="side-form__group-title">{t.drive.slots.paper}</span>
                            <FileInput files={paper} onChange={setPaper} multiple={false} disabled={save.loading} />
                            <p className="side-form__hint">{t.sales.paperHint}</p>
                        </div>

                        {/* Optional: a company paid in one go has no schedule to track, and an empty
                            plan would put a row on a screen nobody asked for. */}
                        <div className="side-form__group">
                            <span className="side-form__group-title">{t.sales.addPlan}</span>
                            <div className="side-form__row side-form__row--pair">
                                <NumberInput value={planCount} onChange={setPlanCount} label={t.sales.planCount} disabled={save.loading} />
                                <NumberInput value={planPeriod} onChange={setPlanPeriod} label={t.sales.planPeriod} disabled={save.loading} />
                            </div>
                            {!!planCount && planCount > 0 && (
                                <DateTimeInput value={planStart} onChange={setPlanStart} label={t.sales.planStart} dateOnly disabled={save.loading} />
                            )}
                        </div>
                    </>
                )}

                {save.error && <p className="side-form__error">{save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={save.loading || !unit || !buyer}>
                    {save.loading ? t.common.loading : mode === "reserve" ? t.sales.reserve : t.sales.sell}
                </button>
            </footer>
        </section>
    );
}
